import { type FC, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { FieldError } from "@/shared/components/ui/FieldError";
import { supabase } from "@/shared/lib/supabase";
import { ONBOARDING_ROUTES } from "@/shared/constants/paths";
import { EventType, trackEvent } from "@/shared/utils/core";
import { sentryUtils, type SentryEventContext } from "@/shared/utils/sentry";
import { toErrorType } from "@/shared/utils/funnelAnalytics";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

export const AccessCodeRedemption: FC<FlowCustomComponentProps> = ({ onComplete, setAnswer }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const trackCheckoutFailed = (err: unknown) =>
    trackEvent(EventType.CHECKOUT_FAILED, {
      flow: "self_serve",
      checkout_context: "onboarding_access_code",
      error_type: toErrorType(err),
    });

  const startCheckout = async (promotionCode: string) => {
    setIsLoading(true);

    let sentryContext: SentryEventContext | undefined;

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setError("We couldn't find your account. Refresh the page and try again.");
        setIsLoading(false);
        return;
      }

      const { data: userRecord } = await supabase
        .from("users")
        .select("user_id")
        .eq("auth_id", user.id)
        .single();

      if (!userRecord) {
        setError("We couldn't find your account. Refresh the page and try again.");
        setIsLoading(false);
        return;
      }

      const { data: userPerson } = await supabase
        .from("user_estates")
        .select("estate_id")
        .eq("user_id", userRecord.user_id)
        .maybeSingle();

      sentryContext = {
        userId: userRecord.user_id,
        estateId: userPerson?.estate_id,
      };

      const currentHost = window.location.origin;
      const returnUrl = `${currentHost}${ONBOARDING_ROUTES.ACCOUNT}?stripe_success=1`;
      const cancelUrl = `${currentHost}${ONBOARDING_ROUTES.ACCOUNT}`;

      const { data, error: checkoutError } = await supabase.functions.invoke("billing/checkout", {
        body: {
          returnUrl,
          cancelUrl,
          userId: userRecord.user_id,
          estateId: userPerson?.estate_id,
          context: "POST_LOSS_ONBOARDING",
          promptLocation: "onboarding_access_code",
          sourcePath: window.location.pathname,
          createCustomer: true,
          mode: "payment",
          promotionCode,
        },
      });

      if (checkoutError) {
        trackCheckoutFailed(checkoutError);
        console.error("Error creating checkout session:", checkoutError);
        setError("Checkout didn't open. Try again.");
        setIsLoading(false);
        return;
      }

      if (data?.url) {
        trackEvent(EventType.CHECKOUT_STARTED, {
          flow: "self_serve",
          checkout_context: "onboarding_access_code",
          has_promotion_code: true,
        });
        setAnswer("access-code", promotionCode);
        setAnswer("has-access-code", "yes");
        window.location.href = data.url;
      } else {
        trackCheckoutFailed(null);
        setError("Checkout didn't open. Try again.");
        setIsLoading(false);
      }
    } catch (error: unknown) {
      trackCheckoutFailed(error);
      console.error("Access code checkout error:", error);
      sentryUtils.captureError(error, "Access code checkout error", sentryContext);
      setError("Your gift code wasn't applied. Try again.");
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (isLoading) return;

    const trimmed = code.trim();
    if (!trimmed) {
      setError("Enter your gift code");
      return;
    }

    setError(null);
    startCheckout(trimmed);
  };

  const handleSkip = () => {
    trackEvent(EventType.ACCESS_CODE_SKIPPED, { flow: "self_serve" });
    setAnswer("has-access-code", "no");
    onComplete();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading) {
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="access-code">Enter your gift code</Label>
        <Input
          id="access-code"
          type="text"
          placeholder="XXXX-XXXX"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        {error && <FieldError message={error} />}
      </div>

      <Button
        variant="brand"
        rounded
        className="w-full"
        onClick={handleSubmit}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            Redeem <ArrowRight className="h-4 w-4" />
          </>
        )}
      </Button>

      <button
        type="button"
        onClick={handleSkip}
        className="text-sm font-medium text-secondary underline underline-offset-2 hover:text-primary"
        disabled={isLoading}
      >
        I don't have it
      </button>
    </div>
  );
};
